import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { useAuth } from '../../context/AuthContext';

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const { currentUser } = useAuth();

  // Inline name editing
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');

  // Load all users from the Firestore 'users' collection
  async function loadUsers() {
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(db, 'users'));
      setUsers(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error loading users:', err);
    }
    setLoading(false);
  }

  useEffect(() => {
    loadUsers();
  }, []);

  // Save edited name to Firestore
  async function saveName(userId) {
    if (!editingName.trim()) return;
    try {
      await updateDoc(doc(db, 'users', userId), { name: editingName.trim() });
      setMessage('✅ Name updated.');
      setEditingId(null);
      loadUsers();
    } catch (err) {
      setMessage('❌ Failed to update name.');
      console.error(err);
    }
  }

  // Toggle a user's admin status
  async function toggleAdmin(user) {
    if (user.id === currentUser.uid) {
      setMessage("⚠️ You can't change your own admin status.");
      return;
    }

    try {
      await updateDoc(doc(db, 'users', user.id), {
        isAdmin: !user.isAdmin
      });
      setMessage(`✅ Updated "${user.email}" — now ${!user.isAdmin ? 'an Admin' : 'a Customer'}.`);
      loadUsers();
    } catch (err) {
      setMessage('❌ Failed to update user role.');
      console.error(err);
    }
  }

  // Format Firestore Timestamp or plain Date to readable string
  function formatDate(timestamp) {
    if (!timestamp) return 'N/A';
    // Firestore Timestamp has .seconds, plain Date objects don't
    const date = timestamp.seconds
      ? new Date(timestamp.seconds * 1000)
      : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  }

  return (
    <div className="container py-5">

      {/* Header */}
      <div className="mb-3">
        <Link to="/admin" className="text-muted small text-decoration-none">← Dashboard</Link>
        <h2 className="fw-bold mb-0">👥 User Management</h2>
        <p className="text-muted small mb-0">
          {users.length} registered user{users.length !== 1 ? 's' : ''} total
        </p>
      </div>

      {message && (
        <div className="alert alert-info alert-dismissible fade show" role="alert">
          {message}
          <button type="button" className="btn-close" onClick={() => setMessage('')} />
        </div>
      )}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" style={{ color: 'var(--piggy-pink)' }} role="status" />
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Joined</th>
                <th>Role</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center text-muted py-5">
                    No users found.
                  </td>
                </tr>
              ) : (
                users.map(user => (
                  <tr key={user.id} className={user.id === currentUser.uid ? 'table-light' : ''}>
                    <td>
                      {editingId === user.id ? (
                        <div className="d-flex align-items-center gap-1">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            style={{ maxWidth: '160px', borderRadius: '2px' }}
                            value={editingName}
                            onChange={e => setEditingName(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') saveName(user.id); if (e.key === 'Escape') setEditingId(null); }}
                            autoFocus
                          />
                          <button className="btn btn-piggy btn-sm" onClick={() => saveName(user.id)}>Save</button>
                          <button className="btn btn-outline-secondary btn-sm" onClick={() => setEditingId(null)}>Cancel</button>
                        </div>
                      ) : (
                        <div className="d-flex align-items-center gap-2">
                          <span>{user.name || <span className="text-muted">—</span>}</span>
                          {user.id === currentUser.uid && (
                            <span className="badge bg-secondary small">You</span>
                          )}
                          <button
                            className="btn btn-link p-0"
                            style={{ fontSize: '0.75rem', color: '#aaa' }}
                            title="Edit name"
                            onClick={() => { setEditingId(user.id); setEditingName(user.name || ''); }}
                          >
                            ✏️
                          </button>
                        </div>
                      )}
                    </td>
                    <td>{user.email}</td>
                    <td className="small text-muted">{formatDate(user.createdAt)}</td>
                    <td>
                      <span className={`badge ${user.isAdmin ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                        {user.isAdmin ? '⚙️ Admin' : '🛒 Customer'}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => toggleAdmin(user)}
                        disabled={user.id === currentUser.uid}
                        title={user.id === currentUser.uid ? "Can't change your own role" : ''}
                      >
                        {user.isAdmin ? 'Remove Admin' : 'Make Admin'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AdminUsers;
